import React, { useMemo } from 'react';
import { Chip } from '@heroui/react';
import { Typography } from '../../../../components';
import { useTranslations } from '../../../../contexts/translation.context';
import { PartnerOrderData } from '../types';
import { GroupUserIc, MoneyIc, TicketBillIc } from '../../../../components/icons';

interface OrderInfoTooltipProps {
  order: PartnerOrderData;
}

const formatCurrency = (value?: number | null) => {
  if (value === undefined || value === null || Number.isNaN(value)) {
    return '$0.00';
  }

  return `$${Number(value).toFixed(2)}`;
};

const OrderInfoTooltip: React.FC<OrderInfoTooltipProps> = ({ order }) => {
  const { t } = useTranslations();

  const typeLabel = useMemo(() => {
    return order.model.type === 'free' ? 'FREE' : 'PAID';
  }, [order.model.type]);

  const description = order.offer.description?.trim();
  const pricePerSubscriber = order.subscribers?.price ?? 0;
  const subscribersCount = order.subscribers?.count ?? 0;
  const totalPrice = order.statistics?.totalPrice ?? pricePerSubscriber * subscribersCount;

  return (
    <div className="flex min-w-[15rem] max-w-[20rem] flex-col gap-3">
      <div className="flex flex-col gap-1">
        <Typography variant="h6" className="!text-auth-ink !text-sm !leading-5 !font-semibold">
          {t('partner.orders.orderInfo.title')}
        </Typography>
        {description ? (
          <Typography variant="body2" className="text-auth-muted text-xsm leading-[1.125rem]">
            {description}
          </Typography>
        ) : (
          <Typography variant="body2" className="text-neutral-500 text-xsm leading-[1.125rem]">
            {t('partner.orders.orderInfo.noDescription')}
          </Typography>
        )}
      </div>

      <Chip
        size="sm"
        radius="lg"
        variant="flat"
        classNames={{
          base: order.model.type === 'free' ? 'bg-husky' : 'bg-[#F5E8FF]',
          content: order.model.type === 'free' ? 'text-partner' : 'text-accentDeep',
        }}
      >
        {typeLabel}
      </Chip>

      <div className="flex items-center gap-4">
        <div className="flex items-center gap-1">
          <TicketBillIc className="text-auth-muted" />
          <Typography variant="body2" className="text-auth-ink text-xsm leading-[1.125rem]">
            {formatCurrency(pricePerSubscriber)}
          </Typography>
        </div>
        <div className="flex items-center gap-1">
          <GroupUserIc className="text-auth-muted" />
          <Typography variant="body2" className="text-auth-ink text-xsm leading-[1.125rem]">
            {subscribersCount}
          </Typography>
        </div>
        <div className="flex items-center gap-1">
          {/* <MoneyIc className="text-auth-muted" /> */}
          <Typography variant="body2" className="text-auth-ink text-xsm leading-[1.125rem]">
            {formatCurrency(totalPrice)}
            <span className="ml-1 text-neutral-500">
              {t('partner.orders.orderInfo.totalSuffix')}
            </span>
          </Typography>
        </div>
      </div>
    </div>
  );
};

export default OrderInfoTooltip;
